import { Children, type ReactNode } from 'react';
import { isElement } from 'react-is';

import type {
  HistogramAnnotationsSlots,
  HistogramSlots,
} from './types/slots.js';
import { Intent } from '../../../core/slots/Intent/Intent.js';
import type { IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { AnnotationsTooltipConfig } from '../../core/components/annotations/components/tooltip/types/annotations-tooltip-props.js';
import type { AnnotationsTrackConfig } from '../../core/components/annotations/types/annotations-track-props.js';
import { populateMissingMarkerProps } from '../../core/components/annotations/utils/populate-missing-marker-props.js';
import { populateMissingTrackProps } from '../../core/components/annotations/utils/populate-missing-track-props.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { ChartToolbar } from '../../core/slots/toolbar/ChartToolbar.js';
import type { ColorRuleProps } from '../../core/types/color-rule.js';
import type { LegendProps } from '../../core/types/legend.js';
import { Annotations } from '../../histogram/slots/annotations/Annotations.js';
import { Marker } from '../../histogram/slots/annotations/Marker.js';
import { AnnotationsTooltip } from '../../histogram/slots/annotations/Tooltip.js';
import { Track } from '../../histogram/slots/annotations/Track.js';
import { Legend } from '../../histogram/slots/Legend.js';
import { Pan } from '../../histogram/slots/Pan.js';
import type { SelectProps } from '../../histogram/slots/Select.js';
import { Select } from '../../histogram/slots/Select.js';
import {
  ThresholdIndicator,
  type HistogramThresholdIndicatorProps,
} from '../../histogram/slots/ThresholdIndicator.js';
import { Toolbar } from '../../histogram/slots/Toolbar.js';
import { XAxis } from '../../histogram/slots/XAxis.js';
import { YAxis } from '../../histogram/slots/YAxis.js';
import { Zoom } from '../../histogram/slots/Zoom.js';
import {
  HistogramChartToolbarProps,
  type HistogramChartXAxisProps,
  type HistogramChartYAxisProps,
} from '../../histogram/types/histogram-props.js';
import { HistogramToolbarSlotProps } from '../../histogram/types/toolbar.js';

const iterateAnnotationsChildren = (
  children: ReactNode,
): {
  tracks: AnnotationsTrackConfig[];
  tooltip: AnnotationsTooltipConfig | undefined;
} => {
  const tracks: AnnotationsTrackConfig[] = [];
  let tooltip: AnnotationsTooltipConfig | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, Track)) {
      const { children: trackChildren, ...trackProps } = child.props;
      const currentTrack: AnnotationsTrackConfig = populateMissingTrackProps({
        ...trackProps,
        markers: [],
      });

      Children.forEach(trackChildren, (markerChild) => {
        if (isElement(markerChild) && isSlot(markerChild, Marker)) {
          currentTrack.markers.push(
            populateMissingMarkerProps(markerChild.props, trackProps),
          );
        }
      });

      tracks.push(currentTrack);
    } else if (isSlot(child, AnnotationsTooltip)) {
      const { children: tooltipChildren, hidden } = child.props;
      tooltip = { template: tooltipChildren, hidden: hidden ?? false };
    }
  });

  return { tracks, tooltip };
};

export const iterateHistogramSlots = (children: ReactNode): HistogramSlots => {
  let disableZoom = false;
  let disablePan = false;
  let legend: LegendProps = {};
  let selectActions: SelectProps['actions'];
  let intents: IntentProps[] | undefined;
  const colorRules: ColorRuleProps[] = [];
  const thresholds: HistogramThresholdIndicatorProps[] = [];
  const yAxis: HistogramChartYAxisProps[] = [];
  let xAxis: HistogramChartXAxisProps = {};
  let toolbar: HistogramToolbarSlotProps = {};
  // Config for the old API Toolbar to be removed with APPDEV-15614
  let deprecatedToolbar: HistogramChartToolbarProps = {};
  let annotations: HistogramAnnotationsSlots | undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, Zoom)) {
      disableZoom = child.props.disabled === true;
    } else if (isSlot(child, Pan)) {
      disablePan = child.props.disabled === true;
    } else if (isSlot(child, Legend)) {
      legend = { ...child.props };
    } else if (isSlot(child, YAxis)) {
      yAxis.push({
        ...child.props,
      });
    } else if (isSlot(child, XAxis)) {
      xAxis = {
        ...child.props,
      };
    } else if (isSlot(child, Select)) {
      selectActions = child.props.actions;
    } else if (isSlot(child, ColorRule) && isValidColorRule(child.props)) {
      colorRules.push(child.props);
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Toolbar)) {
      toolbar = child.props;
      // eslint-disable-next-line @typescript-eslint/no-deprecated
    } else if (isSlot(child, ChartToolbar)) {
      // TODO: Remove deprecated component support (APPDEV-15613)
      deprecatedToolbar = {
        enabled: true,
        ...child.props,
      };
    } else if (isSlot(child, ThresholdIndicator)) {
      thresholds.push(child.props);
    } else if (isSlot(child, Annotations)) {
      const {
        children: annotationsChildren,
        showLabels,
        visibleTracksLimit,
        annotationsActions,
      } = child.props;
      const { tracks, tooltip } =
        iterateAnnotationsChildren(annotationsChildren);
      annotations = {
        tracks,
        tooltip,
        showLabels,
        visibleTracksLimit,
        annotationsActions,
      };
    }
  });

  const interactionSlots: HistogramSlots = {
    disableZoom,
    disablePan,
    legend,
    yAxis,
    xAxis,
    colorRules,
    thresholds,
    toolbar,
    deprecatedToolbar,
  };

  if (selectActions) {
    interactionSlots.selectActions = selectActions;
  }

  if (intents) {
    interactionSlots.intents = intents;
  }

  if (annotations) {
    interactionSlots.annotations = annotations;
  }

  return interactionSlots;
};
